import React, { useCallback, useEffect, useRef } from 'react';
import { CREATIVE_BLOCK, TEXT_BLOCK, HEADER, FOOTER, DYNAMIC_TABLE_BLOCK, STANDALONE_BLOCK, LINK_BLOCK, BUTTON_BLOCK, STATIC_TABLE_BLOCK, HTML_BLOCK } from '../../utils/complexDocumentConstants';
import CreativeBlockDesign from './CreativeBlockDesign';
import DynamicTableDesign from './DynamicTableDesign';
import StandaloneBlock from './StandaloneBlock';
import GridDesign from './GridDesign';
import HtmlBlockRenderer from './HtmlBlockRenderer';
import { useDocumentContext } from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import ComplexDefaultStyleProvider, { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import TextBlockWrapper from './TextBlockWrapper';
import { LinkBlock } from '../ComplexEmailDesign/LinkBlockDesign/LinkBlock';
import { ButtonBlock } from '../ComplexEmailDesign/ButtonBlockDesign/ButtonBlock';
import { ChartIcon, HeadingOneIcon, HeadingThreeIcon, HeadingTwoIcon, HtmlIcon, LinkIcon, ListIcon, ParagraphIcon } from '../../images/svgIcons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { AccessibilitySeparator } from './styles';
import { ACCESSIBILITY_TOOL_TIPS } from './translationValues';

const NAMESPACES = ['ACCESSIBILITY_TOOL_TIPS'];
const getAccessibleIcon = ({ accessibleType, level, row=null, col=null  }, t) => {
  const iconMap = {
    emailSubject: { icon: <ParagraphIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.key, ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.value) },
    documentSummary: { icon: <ParagraphIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.key, ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.value) },
    blockSummary: { icon: <ParagraphIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.key, ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.value) },
    link: { icon: <LinkIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.LINK.key, ACCESSIBILITY_TOOL_TIPS.LINK.value) },
    paragraph: { icon: <ParagraphIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.key, ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.value) },
    dynamicTable: { icon: <ParagraphIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.key, ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.value) },
    list: { icon: <ListIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.LIST.key, ACCESSIBILITY_TOOL_TIPS.LIST.value) },
    pieChart: { icon: <ChartIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.ALTERNATE_TEXT.key, ACCESSIBILITY_TOOL_TIPS.ALTERNATE_TEXT.value, { val: 'Pie chart' }) },
    donutChart: { icon: <ChartIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.ALTERNATE_TEXT.key, ACCESSIBILITY_TOOL_TIPS.ALTERNATE_TEXT.value, { val: 'Donut chart' }) },
    lineChart: { icon: <ChartIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.ALTERNATE_TEXT.key, ACCESSIBILITY_TOOL_TIPS.ALTERNATE_TEXT.value, { val: 'Line chart' }) },
    barChart: { icon: <ChartIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.ALTERNATE_TEXT.key, ACCESSIBILITY_TOOL_TIPS.ALTERNATE_TEXT.value, { val: 'Bar chart' }) },
    html: { icon: <HtmlIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.HTML_INFO.key, ACCESSIBILITY_TOOL_TIPS.HTML_INFO.value) },
  };

  if (accessibleType === 'heading') {
    const headingMap = {
      1: { icon: <HeadingOneIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.HEADING_ONE.key, ACCESSIBILITY_TOOL_TIPS.HEADING_ONE.value) },
      2: { icon: <HeadingTwoIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.HEADING_TWO.key, ACCESSIBILITY_TOOL_TIPS.HEADING_TWO.value) },
      3: { icon: <HeadingThreeIcon />, label: t(ACCESSIBILITY_TOOL_TIPS.HEADING_THREE.key, ACCESSIBILITY_TOOL_TIPS.HEADING_THREE.value) },
    };
    return (
      <Tooltip title={headingMap[level]?.label || t(ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.key, ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.value)}>
        <span>{headingMap[level]?.icon || <ParagraphIcon />}</span>
      </Tooltip>
    );
  }

  if( accessibleType === 'staticTable'){
    return (
      <Tooltip title={t(ACCESSIBILITY_TOOL_TIPS.STATIC_TABLE_INFO.key, ACCESSIBILITY_TOOL_TIPS.STATIC_TABLE_INFO.value, {row: row, col: col})}>
        <span>{<ParagraphIcon/>}</span>
      </Tooltip>
    );
  }

  const { icon, label } = iconMap[accessibleType] || {
    icon: <ParagraphIcon />,
    label: t(ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.key, ACCESSIBILITY_TOOL_TIPS.PARAGRAPH.value),
  };

  return (
    <Tooltip title={label}>
      <span>{icon}</span>
    </Tooltip>
  );
};


export const AccessibilitySeparatorTag = ({accessibleInfo}) => {
  const { t } = useTranslation(NAMESPACES);
  return (
  <AccessibilitySeparator accessibleType= {accessibleInfo.accessibleType}>
      <div className='accessible-tag'>{getAccessibleIcon(accessibleInfo, t)}</div>
  </AccessibilitySeparator>
  )
}

const BlockRenderer = ({lastBlock, isMobileView, ...props}) => {
  const customSelector = useCallback(store => store[props.itemUUID], []);
  const [item] = useDocumentContext(customSelector, `BlockRenderer-${props.itemUUID}`);

  const accessibleTypeSelector = useCallback(store => ({accessibleType :store[props.itemUUID].accessibleType, level: store[props.itemUUID]?.level || null}), []);
  const [accessibleInfo] = useDocumentContext(accessibleTypeSelector, `BlockRenderer-${props.itemUUID}`);

  const checkAccessibilitySelector = useCallback(store => store[CONTEXT_KEYS.CHECK_ACCESSIBILITY] , []);
  const [isAccessibility] = useArtboardContext(checkAccessibilitySelector, `BlockRenderer-${props.itemUUID}`);

  const customStyleSelector = useCallback(store => store[props.itemUUID].style, []);
  const [blockStyle] = useDocumentContext(customStyleSelector, `BlockRenderer-${props.itemUUID}`);
  
  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [defaultStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const settingsVisibleRef = useRef(false);

  const sidebarSelector = useCallback(store => store[CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE] && store[CONTEXT_KEYS.CURRENT_BLOCK_SETTINGS]?.itemUUID === props.itemUUID, []);
  const [blockSettingsVisible, updateArtboard] = useArtboardContext(sidebarSelector, `BlockRenderer-${props.itemUUID}`);

  const [currentBlockEditing] = useArtboardContext(
    (store) => (store[CONTEXT_KEYS.CURRENT_BLOCK]),`BlockRenderer-${props.parentUUID}`);
    
  const customArtboardSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_PAGE] === props.parentUUID && store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE] !== true, []);
  const [blockEditing, updateArtboardcontext] = useArtboardContext(customArtboardSelector, `ComplexBlockRenderer-${props.parentUUID}`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexBlockRenderer-${props.itemUUID}`);

  const itemType = item.type;
  const currentPageEditing = useRef(blockEditing);

  useEffect(() => {
    currentPageEditing.current = blockEditing; // Hoping that setting this first preceeding the next use effect would help
  }, [blockEditing]);

  useEffect(() => {
    settingsVisibleRef.current = blockSettingsVisible
  }, [blockSettingsVisible]);

  useEffect(() => {
    return () => {
      if (settingsVisibleRef.current) {
        updateArtboard({ [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false, [CONTEXT_KEYS.CURRENT_BLOCK_SETTINGS]: null });
      }
    }
  }, []);

  useEffect(() => {
    if (!props.isVisible && currentPageEditing.current && currentBlockEditing===props.itemUUID) {
      updateArtboardcontext({
        [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false,
        [CONTEXT_KEYS.CURRENT_BLOCK]: '',
        [CONTEXT_KEYS.CURRENT_PAGE]: '',
        [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false
      });
    }
  }, [props.isVisible]);
  
  const adjustScroll=() => { // TODO : should use useCallback since this is causing lot of re-rendering
    let myDiv = document.getElementById(`vertical-layout-${props.pageId}`);
    if (myDiv && lastBlock===props.itemUUID) {
      myDiv.scrollTop = myDiv.scrollHeight - myDiv.clientHeight;
    }else {
        console.error(`Element with ID 'vertical-layout-${props.pageId}' not found.`);
    }
  };

  if (isInteractiveDesign) {
    props = {
      ...props,
      editable: props.editable ? item.isStandalone ? false : !!item.isEditable : false,
      isInteractiveDesign: isInteractiveDesign
    };
  }

  const renderBlock = () => {
    if (itemType === CREATIVE_BLOCK || itemType === HEADER || itemType === FOOTER) {
      return (
        <CreativeBlockDesign
          {...props}
          type={itemType}
          getElementStyles={props.getElementStyles}
          adjustScroll={adjustScroll}
          isMobileView={isMobileView}
        />
      );
    } else if (itemType === DYNAMIC_TABLE_BLOCK) {
      return (
        <DynamicTableDesign {...props} type={itemType} getElementStyles={props.getElementStyles} />
      );
    } else if (itemType === STANDALONE_BLOCK) {
      return (
        <StandaloneBlock {...props} type={itemType} getElementStyles={props.getElementStyles} />
      );
    } else if (itemType === TEXT_BLOCK) {
      return <TextBlockWrapper adjustScroll={adjustScroll} itemUUID={props.itemUUID} {...props} />;
    } else if (itemType === LINK_BLOCK) {
      return <LinkBlock {...props} type={itemType} />;
    } else if (itemType === BUTTON_BLOCK) {
      return <ButtonBlock {...props} type={itemType} />;
    } else if (itemType === STATIC_TABLE_BLOCK) {
      return <GridDesign {...props} type={itemType} />;
    } else if (itemType === HTML_BLOCK) {
      return (
        <HtmlBlockRenderer
          {...props}
          type={itemType}
          getElementStyles={props.getElementStyles}
          isMobileView={isMobileView}
        />
      );
    }
    return null;
  };

  const renderBlockWithAccessibility = isAccessibility ? (
    <>
      {renderBlock()}
      {[STANDALONE_BLOCK, DYNAMIC_TABLE_BLOCK, STATIC_TABLE_BLOCK].includes(itemType) ? null : (
        <AccessibilitySeparatorTag accessibleInfo={accessibleInfo} />
      )}
    </>
  ) : renderBlock();

  if (blockStyle?.fontFamily || blockStyle?.fontSize) {
    return (
      <ComplexDefaultStyleProvider customState={{fontSize: blockStyle.fontSize || defaultStyle.fontSize , fontFamily: blockStyle?.fontFamily || defaultStyle.fontFamily}}>
        {renderBlockWithAccessibility}
      </ComplexDefaultStyleProvider>
    );
  }

  return  renderBlockWithAccessibility

};

export default BlockRenderer;
